import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { 
  HelpCircle, 
  MessageCircle, 
  BookOpen, 
  FileText, 
  Users, 
  Mail, 
  Phone, 
  Clock,
  CheckCircle,
  ArrowRight,
  ExternalLink
} from "lucide-react";

export default function HelpCenter() {
  const supportCategories = [
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Resume & Documents",
      description: "Help with resume building, tailoring, and document editing",
      articles: ["How to tailor your resume", "ATS optimization guide", "Resume formatting tips"]
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Account & Billing",
      description: "Manage your account settings, subscriptions, and billing",
      articles: ["Update billing information", "Cancel subscription", "Change password"]
    },
    {
      icon: <BookOpen className="w-6 h-6" />,
      title: "Getting Started",
      description: "New to TailorWiz? Start here for onboarding guides",
      articles: ["First steps guide", "Platform overview", "Feature walkthrough"]
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: "Technical Support",
      description: "Troubleshooting and technical assistance",
      articles: ["Upload issues", "Browser compatibility", "Performance optimization"]
    }
  ];

  const faqs = [
    {
      question: "How do I tailor my resume to a specific job?",
      answer: "Use our Resume Tailoring feature by uploading your resume and the job description. Our AI will analyze both documents and suggest specific changes to improve your match score."
    },
    {
      question: "What file formats are supported for resume uploads?",
      answer: "We support PDF, DOC, and DOCX file formats. For best results, we recommend uploading your resume as a PDF or Word document."
    },
    {
      question: "How does the ATS optimization work?",
      answer: "Our ATS optimization analyzes your resume against common Applicant Tracking System filters, checking for proper formatting, keywords, and structure to ensure your resume passes automated screening."
    },
    {
      question: "Can I download my tailored resume?",
      answer: "Yes! Once your resume is tailored, you can download it in multiple formats including PDF and Word document formats."
    },
    {
      question: "How accurate is the job matching score?",
      answer: "Our AI analyzes hundreds of factors including keywords, skills, experience level, and job requirements to provide a comprehensive match score typically within 5-10% accuracy."
    },
    {
      question: "Is my data secure and private?",
      answer: "Absolutely. We use enterprise-grade encryption and never share your personal information or resume data with third parties. Your documents are stored securely and can be deleted at any time."
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <PageHeader 
        title="Help Center" 
        description="Find answers, get support, and learn how to make the most of TailorWiz"
        backTo="/"
      />

      <div className="container mx-auto px-6 py-8 max-w-6xl">
        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                <MessageCircle className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Contact Support</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Get personalized help from our support team
              </p>
              <Button variant="outline" size="sm" asChild>
                <Link to="/contact">
                  Get Help <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-accent/20 transition-colors">
                <BookOpen className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Documentation</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Comprehensive guides and tutorials
              </p>
              <Button variant="outline" size="sm" asChild>
                <a href="https://payhip.com/resumemagic2025" target="_blank" rel="noopener noreferrer">
                  View Guides <ExternalLink className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-success/20 transition-colors">
                <HelpCircle className="w-6 h-6 text-success" />
              </div>
              <h3 className="text-lg font-semibold mb-2">FAQ</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Quick answers to common questions
              </p>
              <Button variant="outline" size="sm" asChild>
                <Link to="/faq">
                  View FAQ <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Support Categories */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Browse by Category</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {supportCategories.map((category, index) => (
              <Card key={index} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      {category.icon}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{category.title}</CardTitle>
                      <CardDescription>{category.description}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {category.articles.map((article, articleIndex) => (
                      <li key={articleIndex} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-success" />
                        {article}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Popular FAQs */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold mb-6">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent>
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          
          <div className="text-center mt-8">
            <Button variant="outline" asChild>
              <Link to="/faq">
                View All FAQs <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </section>

        {/* Contact Information */}
        <section>
          <div className="bg-gradient-to-r from-primary/5 to-accent/5 rounded-lg p-8">
            <h2 className="text-2xl font-bold mb-6 text-center">Still Need Help?</h2>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mb-3">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold mb-2">Email Support</h3>
                <p className="text-muted-foreground text-sm mb-3">
                  Get detailed help via email
                </p>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/contact">Send Message</Link>
                </Button>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center mb-3">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold mb-2">Live Chat</h3>
                <p className="text-muted-foreground text-sm mb-3">
                  Chat with our support team
                </p>
                <Button variant="outline" size="sm">
                  Start Chat
                </Button>
              </div>

              <div className="flex flex-col items-center">
                <div className="w-12 h-12 bg-success rounded-lg flex items-center justify-center mb-3">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-semibold mb-2">Response Time</h3>
                <p className="text-muted-foreground text-sm mb-3">
                  Typically within 24 hours
                </p>
                <div className="text-sm text-success font-medium">
                  Mon-Fri, 9AM-6PM EST
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}